import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Snackbar } from '@mui/material';
import Layout from './components/Layout/Layout';
import DriverAppLayout from './components/Layout/DriverAppLayout';
import SeoAndScrollManager from './components/SEO/SeoAndScrollManager';
import CookieConsent from './components/UI/CookieConsent';
import PwaInstallPrompt from './components/UI/PwaInstallPrompt';
import Landing from './pages/Landing';
import PublicRides from './pages/PublicRides';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import GoogleCallback from './pages/GoogleCallback';
import DriverHome from './pages/DriverHome';
import Wallet from './pages/Wallet';
import Activity from './pages/Activity';
import Earnings from './pages/Earnings';
import Referrals from './pages/Referrals';
import Fleet from './pages/Fleet';
import Settings from './pages/Settings';
import Support from './pages/Support';
import Onboarding from './pages/Onboarding';
import AdminTopUps from './pages/AdminTopUps';
import PaymentReturn from './pages/PaymentReturn';
import NotFound from './pages/NotFound';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import DataDeletion from './pages/DataDeletion';
import RefundPolicy from './pages/RefundPolicy';
import CommunityPolicy from './pages/CommunityPolicy';
import GrievanceOfficer from './pages/GrievanceOfficer';
import Plans from './pages/Plans';
import InvoiceView from './pages/InvoiceView';
import VerifyRequired from './pages/VerifyRequired';
import { fetchAppConfig } from './services/publicApi';
import { useAuth } from './contexts/AuthContext';
import {
  onForegroundMessage,
  FcmPayload,
  normalizeNotificationSettings,
  onNotificationSettingsChanged,
  type NotificationSettingsSnapshot,
} from './services/notificationService';
import { getSocket } from './services/socketService';
import api from './services/api';
/* Note: The About page is not included in the main app routes since it's meant to be a public-facing page. It can be added to a separate router that handles public routes if needed. */
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { token, isLoading } = useAuth();

  if (isLoading) {
    return <div />;
  }

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicPage: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <Layout>{children}</Layout>
);

const AppPage: React.FC<{ children: React.ReactElement }> = ({ children }) => (
  <ProtectedRoute>
    <DriverAppLayout>{children}</DriverAppLayout>
  </ProtectedRoute>
);


const PublicRidesRoute: React.FC = () => {
  const { token } = useAuth();
  const [freeMode, setFreeMode] = useState<boolean | null>(null);

  useEffect(() => {
    fetchAppConfig()
      .then((config) => setFreeMode(config.freeRidesEnabled))
      .catch(() => setFreeMode(true));
  }, []);

  if (freeMode === null) return <div />;
  if (token) return <Navigate to="/app/rides" replace />;

  return <PublicRides freeMode={freeMode} />;
};

const RidesRoute: React.FC = () => (
  <Layout><PublicRidesRoute /></Layout>
);

const alertKeyForType = (
  type?: string
): keyof NotificationSettingsSnapshot['rideAlerts'] | null => {
  switch (type) {
    case 'newRide':
    case 'new_ride':
      return 'newRide';
    case 'rideUpdate':
    case 'ride_update':
      return 'rideUpdate';
    case 'rideCompletion':
    case 'ride_completion':
      return 'rideCompletion';
    default:
      return null;
  }
};

const notificationAllowed = (
  settings: NotificationSettingsSnapshot | null,
  alertKey: keyof NotificationSettingsSnapshot['rideAlerts'] | null,
  requirePush: boolean
) => {
  if (!settings) return true;
  if (!settings.enabled) return false;
  if (requirePush && !settings.pushNotifications) return false;
  if (alertKey && settings.rideAlerts[alertKey] === false) return false;
  return true;
};

const App: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [fcmAlert, setFcmAlert] = useState<{ title: string; body: string } | null>(null);
  const [newRideNotif, setNewRideNotif] = useState(false);

  // Track latest token/pathname/notif-settings in refs so socket handlers don't go stale
  const tokenRef = useRef(token);
  const locationRef = useRef(location.pathname);
  const prevPathRef = useRef(location.pathname);
  const notifRef = useRef<NotificationSettingsSnapshot | null>(null);

  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);

  const applyFetchedNotificationSettings = useCallback((data: Partial<NotificationSettingsSnapshot>) => {
    notifRef.current = normalizeNotificationSettings(data);
  }, []);

  // Fetch notification settings whenever the user logs in/out
  useEffect(() => {
    if (!token) { notifRef.current = null; return; }
    api.get('/api/settings/notifications')
      .then((res) => applyFetchedNotificationSettings(res.data))
      .catch(() => {
        // On failure leave notifRef unchanged (null on first load = allow; cached value otherwise)
      });
  }, [token, applyFetchedNotificationSettings]);

  useEffect(() => {
    if (!token) return () => {};
    return onNotificationSettingsChanged((settings) => {
      notifRef.current = settings;
    });
  }, [token]);

  // Refresh notif settings when navigating away from the settings page so handlers
  // immediately reflect any changes the user just saved.
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (!token) return;
    if (prev !== '/settings' || location.pathname === '/settings') return;
    api.get('/api/settings/notifications')
      .then((res) => applyFetchedNotificationSettings(res.data))
      .catch(() => {});
  }, [location.pathname, token, applyFetchedNotificationSettings]);

  // Global socket listener: notify logged-in users when a new ride arrives
  // and they're not already on the rides page (the page handles it internally)
  useEffect(() => {
    const socket = getSocket();
    const handleNewRide = () => {
      if (!tokenRef.current) return;
      // Respect the driver's notification preference
      if (!notificationAllowed(notifRef.current, 'newRide', false)) return;
      const path = locationRef.current;
      if (path !== '/rides' && path !== '/app/rides') {
        setNewRideNotif(true);
      }
    };
    socket.on('newRide', handleNewRide);
    return () => { socket.off('newRide', handleNewRide); };
  }, []);

  // Subscribe to foreground FCM messages (tab is open and focused)
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    onForegroundMessage((payload: FcmPayload) => {
      const alertKey = alertKeyForType(payload.data?.type);
      if (!notificationAllowed(notifRef.current, alertKey, true)) return;
      const title = payload.notification?.title || 'New Ride Available';
      const body = payload.notification?.body || '';
      setFcmAlert({ title, body });
    }).then((fn) => {
      unsubscribe = fn;
    });

    return () => {
      unsubscribe?.();
    };
  }, []);

  return (
    <>
      <SeoAndScrollManager />
      <Routes>
        <Route path="/" element={<PublicPage><Landing /></PublicPage>} />
        <Route path="/rides" element={<RidesRoute />} />
        <Route path="/app/rides" element={<AppPage><PublicRides /></AppPage>} />
        <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
        <Route path="/plans" element={<PublicPage><Plans /></PublicPage>} />
        <Route
          path="/invoice/:paymentOrderId"
          element={
            <AppPage>
              <InvoiceView />
            </AppPage>
          }
        />
        <Route path="/login" element={<PublicPage><Login /></PublicPage>} />
        <Route path="/signup" element={<PublicPage><Signup /></PublicPage>} />
        <Route path="/forgot-password" element={<PublicPage><ForgotPassword /></PublicPage>} />
        <Route path="/reset-password" element={<PublicPage><ResetPassword /></PublicPage>} />
        <Route path="/verify-email" element={<PublicPage><VerifyEmail /></PublicPage>} />
        <Route path="/verify-required" element={<VerifyRequired />} />
        <Route path="/auth/google/callback" element={<PublicPage><GoogleCallback /></PublicPage>} />
        <Route path="/about" element={<PublicPage><About /></PublicPage>} />
        <Route path="/faq" element={<PublicPage><FAQ /></PublicPage>} />
        <Route path="/contact" element={<PublicPage><Contact /></PublicPage>} />
        <Route path="/privacy" element={<PublicPage><Privacy /></PublicPage>} />
        <Route path="/terms" element={<PublicPage><Terms /></PublicPage>} />
        <Route path="/data-deletion" element={<PublicPage><DataDeletion /></PublicPage>} />
        <Route path="/refund-policy" element={<PublicPage><RefundPolicy /></PublicPage>} />
        <Route path="/community-policy" element={<PublicPage><CommunityPolicy /></PublicPage>} />
        <Route path="/grievance-officer" element={<PublicPage><GrievanceOfficer /></PublicPage>} />
        <Route path="/payment/return" element={<PublicPage><PaymentReturn /></PublicPage>} />
        <Route
          path="/app"
          element={
            <AppPage>
              <DriverHome />
            </AppPage>
          }
        />
        <Route
          path="/wallet"
          element={
            <AppPage>
              <Wallet />
            </AppPage>
          }
        />
        <Route
          path="/activity"
          element={
            <AppPage>
              <Activity />
            </AppPage>
          }
        />
        <Route
          path="/earnings"
          element={
            <AppPage>
              <Earnings />
            </AppPage>
          }
        />
        <Route
          path="/referrals"
          element={
            <AppPage>
              <Referrals />
            </AppPage>
          }
        />
        <Route
          path="/fleet"
          element={
            <AppPage>
              <Fleet />
            </AppPage>
          }
        />
        <Route
          path="/settings"
          element={
            <AppPage>
              <Settings />
            </AppPage>
          }
        />
        <Route
          path="/admin/topups"
          element={
            <AppPage>
              <AdminTopUps />
            </AppPage>
          }
        />
        <Route
          path="/admin/top-ups"
          element={
            <ProtectedRoute>
              <Navigate to="/admin/topups" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <AppPage>
              <Support />
            </AppPage>
          }
        />
        <Route
          path="/onboarding"
          element={
            <AppPage>
              <Onboarding />
            </AppPage>
          }
        />
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <Navigate to="/settings?tab=preferences" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Navigate to="/settings?tab=profile" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Navigate to="/settings?tab=notifications" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/unlocks"
          element={
            <ProtectedRoute>
              <Navigate to="/activity" replace />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<PublicPage><NotFound /></PublicPage>} />
      </Routes>

      {/* Foreground push notification toast */}
      <PwaInstallPrompt />
      <Snackbar
        open={!!fcmAlert}
        autoHideDuration={6000}
        onClose={() => setFcmAlert(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setFcmAlert(null)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          <strong>{fcmAlert?.title}</strong>
          {fcmAlert?.body && (
            <>
              <br />
              {fcmAlert.body}
            </>
          )}
        </Alert>
      </Snackbar>
      {/* New ride alert for logged-in users on other pages */}
      <Snackbar
        open={newRideNotif}
        autoHideDuration={8000}
        onClose={() => setNewRideNotif(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNewRideNotif(false)}
          severity="success"
          variant="filled"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => { navigate('/app/rides'); setNewRideNotif(false); }}
            >
              View rides
            </Button>
          }
          sx={{ width: '100%' }}
        >
          New ride request available
        </Alert>
      </Snackbar>
      <CookieConsent />
    </>
  );
};

export default App;
